import { supabase } from "@/integrations/supabase/client";

interface SnapshotData {
  sites?: any[];
  zones?: any[];
  systems?: any[];
  endpoints?: any[];
  cloud_services?: any[];
  site_cloud_links?: any[];
  conduits?: any[];
  risks?: any[];
  controls?: any[];
  incidents?: any[];
  evidence?: any[];
  tasks?: any[];
  suppliers?: any[];
  access_relations?: any[];
  asset_relations?: any[];
  asset_templates?: any[];
  links?: any[];
  standards?: any[];
}

const TABLES_TO_SNAPSHOT = [
  'sites',
  'zones',
  'systems',
  'endpoints',
  'cloud_services',
  'site_cloud_links',
  'conduits',
  'risks',
  'controls',
  'incidents',
  'evidence',
  'tasks',
  'suppliers',
  'access_relations',
  'asset_relations',
  'asset_templates',
  'links',
  'standards',
] as const;

export const snapshotService = {
  async createSnapshot(
    name: string,
    description: string = '',
    type: 'manual' | 'auto' | 'pre-restore' | 'pre-import' = 'manual'
  ) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get user's org_id
    const { data: profile } = await supabase
      .from('profiles')
      .select('org_id')
      .eq('user_id', user.id)
      .single();

    if (!profile?.org_id) throw new Error('No organization found');

    // Collect all data from tables
    const snapshotData: SnapshotData = {};
    const recordCounts: Record<string, number> = {};
    const fileReferences: string[] = [];

    for (const table of TABLES_TO_SNAPSHOT) {
      const { data, error } = await supabase
        .from(table)
        .select('*')
        .eq('org_id', profile.org_id);

      if (error) {
        console.error(`Error fetching ${table}:`, error);
        continue;
      }

      snapshotData[table] = data || [];
      recordCounts[table] = data?.length || 0;

      // Track file references from evidence
      if (table === 'evidence' && data) {
        data.forEach((item: any) => {
          if (item.storage_path) {
            fileReferences.push(item.storage_path);
          }
        });
      }
    }

    // Calculate approximate size
    const jsonString = JSON.stringify(snapshotData);
    const sizeBytes = new Blob([jsonString]).size;

    // Create snapshot record
    const { data: snapshot, error } = await supabase
      .from('snapshots')
      .insert({
        org_id: profile.org_id,
        name,
        description,
        snapshot_type: type,
        created_by: user.id,
        data: snapshotData as any,
        record_counts: recordCounts as any,
        file_references: fileReferences as any,
        size_bytes: sizeBytes,
      })
      .select()
      .single();

    if (error) throw error;
    return snapshot;
  },

  async restoreSnapshot(snapshotId: string) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    // Get snapshot
    const { data: snapshot, error: fetchError } = await supabase
      .from('snapshots')
      .select('*')
      .eq('id', snapshotId)
      .single();

    if (fetchError) throw fetchError;
    if (!snapshot) throw new Error('Snapshot not found');

    const snapshotData = snapshot.data as SnapshotData;

    // Delete all existing data (in reverse order to respect foreign keys)
    const reverseTables = [...TABLES_TO_SNAPSHOT].reverse();
    for (const table of reverseTables) {
      const { error } = await supabase
        .from(table)
        .delete()
        .eq('org_id', snapshot.org_id);

      if (error) {
        console.error(`Error deleting ${table}:`, error);
      }
    }

    // Restore data from snapshot
    for (const table of TABLES_TO_SNAPSHOT) {
      const tableData = snapshotData[table];
      if (!tableData || tableData.length === 0) continue;

      const { error } = await supabase
        .from(table)
        .insert(tableData);

      if (error) {
        console.error(`Error restoring ${table}:`, error);
        throw new Error(`Failed to restore ${table}: ${error.message}`);
      }
    }

    return snapshot;
  },

  async listSnapshots() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: profile } = await supabase
      .from('profiles')
      .select('org_id')
      .eq('user_id', user.id)
      .single();

    if (!profile?.org_id) throw new Error('No organization found');

    const { data, error } = await supabase
      .from('snapshots')
      .select('id, name, description, snapshot_type, created_at, created_by, record_counts, size_bytes')
      .eq('org_id', profile.org_id)
      .order('created_at', { ascending: false });

    if (error) throw error;
    return data || [];
  },

  async deleteSnapshot(snapshotId: string) {
    const { error } = await supabase
      .from('snapshots')
      .delete()
      .eq('id', snapshotId);

    if (error) throw error;
  },

  async getSnapshotDetails(snapshotId: string) {
    const { data, error } = await supabase
      .from('snapshots')
      .select('*')
      .eq('id', snapshotId)
      .single();

    if (error) throw error;
    return data;
  },

  async cleanupAutoSaves(keepCount: number = 7) {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: profile } = await supabase
      .from('profiles')
      .select('org_id')
      .eq('user_id', user.id)
      .single();

    if (!profile?.org_id) return;

    // Get all auto-saves
    const { data: autoSaves } = await supabase
      .from('snapshots')
      .select('id')
      .eq('org_id', profile.org_id)
      .eq('snapshot_type', 'auto')
      .order('created_at', { ascending: false });

    if (!autoSaves || autoSaves.length <= keepCount) return;

    // Delete old auto-saves
    const toDelete = autoSaves.slice(keepCount).map(s => s.id);
    if (toDelete.length > 0) {
      await supabase
        .from('snapshots')
        .delete()
        .in('id', toDelete);
    }
  },

  async getStorageStats() {
    const { data: { user } } = await supabase.auth.getUser();
    if (!user) throw new Error('Not authenticated');

    const { data: profile } = await supabase
      .from('profiles')
      .select('org_id')
      .eq('user_id', user.id)
      .single();

    if (!profile?.org_id) throw new Error('No organization found');

    // Call the database function to get comprehensive storage stats
    const { data, error } = await supabase
      .rpc('get_org_storage_stats', { org_uuid: profile.org_id });

    if (error) {
      console.error('Error fetching storage stats:', error);
      throw error;
    }

    const stats = data?.[0] || { 
      file_storage_bytes: 0, 
      snapshot_storage_bytes: 0, 
      total_storage_bytes: 0 
    };

    const limitBytes = 209715200; // 200MB in bytes
    const percentage = (stats.total_storage_bytes / limitBytes) * 100;

    return {
      used: stats.total_storage_bytes,
      fileStorageUsed: stats.file_storage_bytes,
      snapshotStorageUsed: stats.snapshot_storage_bytes,
      limit: limitBytes,
      percentage: Math.min(percentage, 100),
    };
  },
};
